import { useMemo } from 'react';
import { hasAnyPermission, checkPermissions } from 'react/utils/utils';

/**
 * Custom hook to handle route-related permissions
 *
 * This hook centralizes all permission checks related to routes and their assignments,
 * making the permission logic reusable across components and easier to maintain.
 *
 * @returns {Object} - Object containing route permissions information
 */
export const useRoutePermissions = () => {
  const canEditDriverAssignments = useMemo(
    () => hasAnyPermission(['ROUTE_ASSIGN_DRIVER', 'SPEED_ASSIGNMENT_LIST']),
    []
  );

  const canEditEquipmentAssignments = useMemo(
    () => hasAnyPermission(['ROUTE_ASSIGN_EQUIPMENT', 'SPEED_ASSIGNMENT_LIST']),
    []
  );

  const canEditTagAssignments = useMemo(
    () => checkPermissions('ROUTE_ASSIGN_TAG'),
    []
  );

  const canEditDriver = useMemo(() => checkPermissions('DRIVER_UPDATE'), []);

  const canEditEquipment = useMemo(
    () => checkPermissions('EQUIPMENT_UPDATE'),
    []
  );

  const canEditTag = useMemo(() => checkPermissions('TAG_UPDATE'), []);

  return {
    canEditDriverAssignments,
    canEditEquipmentAssignments,
    canEditTagAssignments,
    canEditDriver,
    canEditEquipment,
    canEditTag,

    /**
     * Helper function to get permissions for a specific assignment type
     * This simplifies component logic by providing consistent permission objects
     *
     * @param {string} type - The assignment type ('driver', 'equipment', or 'tag')
     * @returns {Object} - Object with canEditAssignment and canEditItem permissions
     */
    getAssignmentPermissions: (type) => {
      switch (type) {
        case 'driver':
          return {
            canEditAssignment: canEditDriverAssignments,
            canEditItem: canEditDriver
          };
        case 'equipment':
          return {
            canEditAssignment: canEditEquipmentAssignments,
            canEditItem: canEditEquipment
          };
        case 'tag':
          return {
            canEditAssignment: canEditTagAssignments,
            canEditItem: canEditTag
          };
        default:
          return {
            canEditAssignment: false,
            canEditItem: false
          };
      }
    }
  };
};
